import path from 'path';

import { PaymentTypeEnum } from '~features/finances';
import { BaseReferenceResponse } from '~interfaces/responses';
import { TopologyService } from '~services';

import Policy from '../interfaces/policy';

class PoliciesService extends TopologyService {
	public readonly path = 'policies';

	constructor() {
		super('v1');
	}

	async getPolicyById(policyId: string): Promise<Policy> {
		const { data } = await this._client.get<PolicyResponse>(path.join(this.path, policyId));

		return PoliciesService.fromResponse(data);
	}

	static fromResponse(data: PolicyResponse): Policy {
		const {
			id,
			name,
			organisationReference,
			requireReservationComment,
			isDriversLicenseVerificationRequired,
			requiredParkingLicensePlateNumber,
			supportedPaymentType,
			...rest
		} = data;

		return {
			...rest,
			...PoliciesService.fromBaseReferenceResponse({ id: id, name: name }),
			requireBookingComment: requireReservationComment,
			requireLicenseVerification: isDriversLicenseVerificationRequired,
			requireParkingLicensePlateNumber: requiredParkingLicensePlateNumber === 'required',
			organisation: PoliciesService.fromBaseReferenceResponse(organisationReference),
			supportedPaymentType: supportedPaymentType as PaymentTypeEnum,
		};
	}
}

interface PolicyResponse extends BaseReferenceResponse {
	organisationReference: BaseReferenceResponse;
	minBookingDurationMinutes?: number;
	maxBookingDurationMinutes?: number;
	minDurationBeforeCancelMinutes: number;
	minDurationBeforeStartMinutes?: number;
	maxDurationBeforeStartMinutes?: number;
	requireMinimumAge?: number;
	allowPersonalBilling: boolean;
	allowExtending: boolean;
	requiredLicenseStatus: string;
	personalBillingDeposit: number;
	requireHostConfirmation: boolean;
	requireReservationComment: boolean;
	requireReservationCostcenter: boolean;
	requireOvertimeValidation: boolean;
	requireDutchLicense: boolean;
	cooldownBufferMinutes: number;
	isDriversLicenseVerificationRequired: boolean;
	requiredParkingLicensePlateNumber: 'notApplicable' | 'optional' | 'required';
	overtimeGracePeriodMinutes: number;
	openHoursRestrictionType: 'none' | 'create' | 'usage';
	supportedPaymentType: 'billingPeriod' | 'oneOffPayment';
}

export default PoliciesService;
